import React, { useContext, useEffect, useMemo, useState } from 'react'
import ProofOfDocument from '../../BusinessLiteForms/components/ProofOfDocument'
import { DialogContext } from '../../../../../store/context/DialogContext'
import { doGET } from '../../../../../util/HttpUtil'
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew'
import { IPLUS_WIZARD } from '../../IPlushelper';
import useDocTypeRules from '../../../../../hooks/useDocTypeRules';
import { DOC_TYPE_FORM_TYPES } from '../../../../../util/docTypeRuleDefaults';
import { useDocTypes } from '../../../../../util/docTypeRuleMeta';

const buildPeriodicPrimeFallbackDocTypes = (data = {}) => {
    const docs = [
        'shareholders',
        'corporateStructureChart',
        'organizationalChart',
        'flowOfFundsDiagram',
    ];

    if (data?.hasMsbLicense || data?.hasLicense) {
        docs.push('license');
    }
    if (data?.hasAmlProgram) {
        docs.push('amlProgram');
    }
    if (data?.amlCftReviewed) {
        docs.push('amlReview');
    }
    if (data?.hasComplaintsPolicy) {
        docs.push('compliancePolicy');
    }
    if (data?.crypto) {
        docs.push('businessAgreement');
    }

    return Array.from(new Set(docs));
};

const DocumentsPages = ({
    isAskForInfo,
    remark,
    recordId,
    asLabel,
    setHasError,
    handleUpdate,
    data,
    pageNum,
    loading,
    step,
    decrementStep,
    handleSubmit,
    applicationForm,
    handlePrev
}) => {
    const { showError } = useContext(DialogContext)
    const [documentErrors, setDocumentErrors] = useState({
        // businessPlan: '',
        shareholders: '',
        // principleProofOfAddress: '',
        // SourceOfBusFundsProof: '',
        corporateStructureChart: '',
        organizationalChart: '',
        license: '',
        amlProgram: '',
        amlReview: '',
        compliancePolicy: '',
        flowOfFundsDiagram: '',
        businessAgreement: ''
    });
    const docRuleApplicationData = useMemo(
        () => ({ ...data, applicationForm }),
        [applicationForm, data],
    );
    const {
        docTypes: ruleDocTypes,
        loading: docRuleLoading,
        error: docRuleError,
    } = useDocTypeRules({
        formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
        applicationData: docRuleApplicationData,
    });
    const { getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();

    useEffect(() => {
        if (docRuleError) {
            showError(docRuleError?.message || "Failed to load document rules.");
        }
    }, [docRuleError, showError]);

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    const docTypesToRender = useMemo(
        () =>
            Array.from(
                new Set(ruleDocTypes?.length ? ruleDocTypes : buildPeriodicPrimeFallbackDocTypes(data)),
            ),
        [ruleDocTypes, data],
    );

    const shouldSubmit = useMemo(() => {
        return applicationForm?.cyclosUserId ? true : false;
    }, [applicationForm?.cyclosUserId]);

    const [docLoading, setDocloading] = useState(false);

    const checkIsDocumentUploaded = async (field) => {
        try {
            const response = await doGET(`/api/application-doc/grid?form_id=${recordId}&type=${field}`);
            if (response?.status === 200) {
                if (response?.data?.rows[0]?.attachment?.urls?.length) {
                    return true
                }
            }
        } catch (error) {
            showError(error)
        }
        return false
    }

    const validateDocuments = async () => {
        const errors = { ...documentErrors };
        let hasAnyError = false;
        let firstErrorMessage = null;

        setDocloading(true);
        for (const docType of docTypesToRender) {
            const meta = getMeta(docType, { scope: 'business' });
            try {
                const isUploaded = await checkIsDocumentUploaded(meta.type);
                if (!isUploaded) {
                    hasAnyError = true;
                    errors[meta.type] = meta.errorMessage;

                    if (!firstErrorMessage) {
                        firstErrorMessage = meta.errorMessage;
                    }
                } else {
                    errors[meta.type] = '';
                }
            } catch (error) {
                hasAnyError = true;
                errors[meta.type] = `Error checking document: ${meta.type}`;
                if (!firstErrorMessage) {
                    firstErrorMessage = `Error checking document: ${meta.type}`;
                }
            }
        }

        if (firstErrorMessage) {
            setDocloading(false);
            showError(firstErrorMessage);
        }

        setDocumentErrors(errors);
        setDocloading(false);
        return !hasAnyError;
    };

    const handleSubmitDoc = async () => {
        const isValid = await validateDocuments();
        if (isValid) {
            setHasError(false);
            await handleUpdate({
                // nextStep: { wizard: isKycVerificationRequired ? IPLUS_WIZARD.KYCVerification : IPLUS_WIZARD.ThankYou },
                skipUpdate: true,
                isLastStep: true,
                isfetchDetail: true
            });
        }
    };

    return (
        <div className="row">
            <div className="col-12">
                <div className='mb-3'>
                {(docRuleLoading || docTypesLoading) && (
                    <div className='d-flex align-items-center justify-content-center py-3 text-muted small'>
                        Loading document rules…
                    </div>
                )}

                {!docRuleLoading && !docTypesLoading &&
                    docTypesToRender.map((docType) => {
                        const meta = getMeta(docType, { scope: 'business' });
                        return (
                            <ProofOfDocument
                                key={docType}
                                dataTestId={meta.dataTestId}
                                isAskForInfo={isAskForInfo}
                                    remark={remark}
                                    recordId={recordId}
                                    asLabel={asLabel}
                                    label={meta.label}
                                    field={meta.field}
                                    type={meta.type}
                                    error={documentErrors[meta.type]}
                                    supportedFiles={meta.supportedFiles}
                                    validateByAi={meta.validateByAi}
                                />
                            );
                        })}
                </div>

                <div className='mt-4'>
                    <ButtonGroupNew
                        isDisabled={loading || docLoading || docRuleLoading || docTypesLoading}
                        pageNum={pageNum}
                        showPageNum={false}
                        loading={loading || docLoading || docRuleLoading || docTypesLoading}
                        asLabel={asLabel}
                        onNextClick={handleSubmitDoc}
                        step={step}
                        onBackClick={() => handlePrev()}
                    />
                </div>
            </div>
        </div>
    )
}

export default DocumentsPages;
